import { useUser } from '../../../Context/useUser';
import { useParams } from 'react-router';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';

import styles from './TestsPage.module.css';
import Line from '../../../Components/Charts/Line/Line';
import { useEffect, useMemo, useState } from 'react';
import API from '../../../Utils/api';
import Table from '../../../Components/Table/Table';
import { toast } from 'react-toastify';

const TEST_TYPE_LABELS = {
    'Type:25': '25M-400M',
    'Type:30': 'T30 & 50M',
    'Type:50': '50M - 100M - 200M - 400M'
};

export default function AllTestsPage () {
    const columns = [
        {
            Header: 'Date',
            accessor: 'testDate',
            Cell: ({ row }) => {
                const date = new Date(row.original?.testDate);
                return (
                    <div>
                        {date.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        })}
                    </div>
                );
            }
        },
        {
            id: 'test',
            Header: () => <div style={{ textAlign: 'center' }}>Test</div>,
            accessor: 'type',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{TEST_TYPE_LABELS[row.original?.type] || 'N/A'}</div>
        },
        {
            id: 'pool',
            Header: () => <div style={{ textAlign: 'center' }}>Pool</div>,
            accessor: 'pool',
            Cell: ({ row }) => (
                <div style={{ textAlign: 'center' }}>{row.original.poolSize ? row.original.poolSize + 'm' : 'N/A'}</div>
            )
        },
        {
            id: 'css',
            Header: () => <div style={{ textAlign: 'center' }}>Critical Swim Speed (CSS)</div>,
            accessor: 'css',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.css?.toFixed(2)}</div>
        },
        {
            id: 'mss',
            Header: () => <div style={{ textAlign: 'center' }}>Maximum Swim Speed (MSS)</div>,
            accessor: 'mss',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.mss?.toFixed(2)}</div>
        }
    ];

    const { user } = useUser();

    const { athleteId } = useParams();

    const visitedUser = useVisitingUser(athleteId);

    const currentUser = athleteId ? visitedUser : user;

    const [allTests, setAllTests] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    const handleRowClick = row => {
        setSelectedRows(prev => {
            const alreadySelected = prev.some(r => r._id === row._id);
            if (alreadySelected) {
                return prev.filter(r => r._id !== row._id);
            } else {
                return [...prev, row];
            }
        });
    };

    const chartData = useMemo(() => {
        const data = selectedRows.length > 0 ? selectedRows : allTests;
        // Sort by testDate ascending
        return [...data].sort((a, b) => new Date(a.testDate) - new Date(b.testDate));
    }, [selectedRows, allTests]);
    
    const fetchAndSetData = async currentUser => {
        if (!currentUser) return;

        try {
            const { data } = await API.getAllTestsForUser(currentUser._id);
            if (data) {
                setAllTests(data);
            }
        } catch (error) {
            toast.error(`Failed to fetch tests - please contact support`, {
                position: 'bottom-center'
            });
        }
    };

    const labelsMeters = useMemo(
        () =>
            chartData.map(record => {
                const date = new Date(record.testDate);
                // Format: dd/mm/yyyy hh:mm (no seconds)
                return date.toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                });
            }),
        [chartData]
    );

    const datasetsMeters = useMemo(
        () => [
            {
                label: 'Critical Swim Speed (CSS)',
                data: chartData.map(record => record.css),
                borderColor: 'rgb(54, 162, 235)',
                backgroundColor: 'rgba(54, 162, 235, 0.5)',
                tension: 0.3
            },
            {
                label: 'Maximum Swim Speed (MSS)',
                data: chartData.map(record => record.mss),
                borderColor: 'rgb(255, 99, 132)',
                backgroundColor: 'rgba(255, 99, 132, 0.5)',
                tension: 0.3
            }
        ],
        [chartData]
    );

    useEffect(() => {
        fetchAndSetData(currentUser);
    }, [currentUser]);

    useEffect(() => {
        setSelectedRows(allTests);
    }, [allTests]);
    return (
        <>
            <div className={styles.pageWrap + ' island'}>
                <Table
                    onRowClick={handleRowClick}
                    className={styles.allTestsTable}
                    columns={columns}
                    data={allTests}
                    rowsPerPage={10}
                    updatePageSize={false}
                    pageSkipping={false}
                    selected={selectedRows}
                    showSelectionIndicator={true}
                />
            </div>
            <div
                className={styles.pageWrap + ' island'}
                style={{ marginTop: '15px', padding: '15px' }}
            >
                <Line
                    className={styles.chart}
                    title={'CSS & MSS over time'}
                    titleY={'Velocity(m/sec)'}
                    titleX={'Date (dd/mm/yyyy hh:mm:ss)'}
                    datasets={datasetsMeters}
                    labels={labelsMeters}
                    xAxisType='category'
                />
            </div>
        </>
    );
}
